import React from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import Text from '~/components/core/TextComponents/Text';
import { DatePickerTextFieldFormik } from '~/components/TextFieldFormik';

import TEXTS from './texts';

interface IncomeProtectionCreateNotificationDialogProps {
  title: string;
  subtitle?: string;
  initialDueDate?: string | null;
  notificationTypeDisabled: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (values: { due_date: string }) => Promise<unknown>;
}

const IncomeProtectionCreateNotificationDialogFormInner = ({
  subtitle,
  onClose,
  notificationTypeDisabled,
}: {
  subtitle?: string;
  onClose: () => void;
  notificationTypeDisabled: boolean;
}) => {
  const { isSubmitting } = useFormikContext<{ due_date: string }>();
  const submitDisabled = notificationTypeDisabled || isSubmitting;

  return (
    <Form className="grid gap-24">
      {subtitle ? <Text variant={Text.VARIANTS.SM}>{subtitle}</Text> : null}
      {notificationTypeDisabled ? (
        <AlertBanner alertType={AlertBanner.ALERT_TYPES.WARNING} note={TEXTS.notificationDisabledWarning} withIcon />
      ) : null}
      <DatePickerTextFieldFormik id="due_date" label={TEXTS.notificationDueDateLabel} className="w-full" clearable />
      <div className="flex justify-end gap-16">
        <Button type="button" variant="outlined" color="primary" onClick={onClose} disabled={isSubmitting}>
          {TEXTS.cancel}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={submitDisabled}>
          {TEXTS.setNotification}
        </Button>
      </div>
    </Form>
  );
};

const IncomeProtectionCreateNotificationDialog = ({
  title,
  subtitle,
  initialDueDate,
  notificationTypeDisabled,
  open,
  onClose,
  onSubmit,
}: IncomeProtectionCreateNotificationDialogProps) => {
  return (
    <CardDialog isDialog open={open} onClose={onClose} fullWidth maxWidth="sm" title={title} titleVariant="h1">
      <Formik
        initialValues={{ due_date: initialDueDate ?? '' }}
        validationSchema={Yup.object().shape({
          due_date: Yup.date().nullable().required(TEXTS.required),
        })}
        onSubmit={onSubmit}
      >
        <IncomeProtectionCreateNotificationDialogFormInner
          subtitle={subtitle}
          onClose={onClose}
          notificationTypeDisabled={notificationTypeDisabled}
        />
      </Formik>
    </CardDialog>
  );
};

export default IncomeProtectionCreateNotificationDialog;
